/* iter-p3-r — AI Books browsing view.
 *
 * Genre chips → clicking one shows just AI-assisted titles in that
 * genre.  Deep-links from the top-level "AI Books" chip on the
 * bookstore landing.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Sparkles, BookOpen, HelpCircle } from 'lucide-react';
import { useSeoMeta } from '@/lib/useSeoMeta';
import BookCard from '@/components/bookstore/BookCard';
import BookDetailModal from '@/components/bookstore/BookDetailModal';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AIBooksPage() {
  useSeoMeta('/bookstore/ai-books', {
    title:       'AI Books · Crown & Rose',
    description: 'Indie novels transparently produced with AI assistance. Filter by genre. Every title carries an Editing Record documenting how the manuscript was made.',
    keywords:    'AI books, AI-assisted novels, transparent AI fiction, indie AI books, ai novel bookstore, Crown and Rose AI',
  });
  const [buckets, setBuckets] = useState(null);
  const [activeGenre, setActiveGenre] = useState(null);
  const [openBook, setOpenBook] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/bookstore/ai-books`)
      .then(r => setBuckets(r.data?.buckets || []))
      .catch(() => setBuckets([]));
  }, []);

  const visibleBuckets = activeGenre
    ? (buckets || []).filter(b => b.genre?.slug === activeGenre)
    : (buckets || []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="ai-books-page">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <Link to="/bookstore" className="inline-flex items-center gap-2 text-[13px] text-muted-foreground hover:text-foreground" data-testid="ai-books-back">
            <ArrowLeft className="h-4 w-4" /> Back to bookstore
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/faq" className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-[11.5px] hover:border-primary hover:text-primary" data-testid="ai-books-faq">
              <HelpCircle className="h-3.5 w-3.5" /> FAQ
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="mb-6 flex items-start gap-3">
          <Sparkles className="mt-1 h-6 w-6 text-primary" />
          <div>
            <h1 className="font-serif text-4xl sm:text-5xl leading-tight">AI Books</h1>
            <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-muted-foreground">
              Indie novels transparently produced with AI assistance. Every title carries a downloadable Editing Record documenting exactly which portions are AI-generated versus human-edited — aligned with the U.S. Copyright Office, UK IPO, and EU AI Act disclosure standards.
            </p>
          </div>
        </div>

        {buckets === null && (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-[12.5px] text-muted-foreground" data-testid="ai-books-loading">
            Loading titles…
          </div>
        )}
        {buckets && buckets.length === 0 && (
          <div className="rounded-md border border-dashed border-border p-8 text-center" data-testid="ai-books-empty">
            <BookOpen className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
            <div className="font-semibold">No AI-assisted titles yet.</div>
            <div className="mt-1 text-[12px] text-muted-foreground">
              Check back soon — writers are shipping new work every week. In the meantime, <Link to="/bookstore" className="text-primary hover:underline">browse the main catalog</Link>.
            </div>
          </div>
        )}

        {buckets && buckets.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2" data-testid="ai-books-genre-chips">
            <button
              type="button"
              onClick={() => setActiveGenre(null)}
              data-testid="ai-books-chip-all"
              className={`rounded-full border px-3 py-1 text-[12px] font-semibold ${activeGenre === null ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-foreground hover:border-primary'}`}
            >
              All ({buckets.reduce((s, b) => s + (b.books?.length || 0), 0)})
            </button>
            {buckets.map((b) => (
              <button
                key={b.genre?.slug || b.genre?.id}
                type="button"
                onClick={() => setActiveGenre(b.genre?.slug)}
                data-testid={`ai-books-chip-${b.genre?.slug}`}
                className={`rounded-full border px-3 py-1 text-[12px] font-semibold ${activeGenre === b.genre?.slug ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-foreground hover:border-primary'}`}
              >
                {b.genre?.name} ({b.books?.length || 0})
              </button>
            ))}
          </div>
        )}

        {visibleBuckets.map((b) => (
          <section key={b.genre?.slug || b.genre?.id} className="mb-10" data-testid={`ai-books-bucket-${b.genre?.slug}`}>
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="font-serif text-2xl">{b.genre?.name}</h2>
              {b.genre?.slug && (
                <Link to={`/bookstore/g/${b.genre.slug}`} className="text-[11.5px] text-primary hover:underline">
                  See all in {b.genre.name} →
                </Link>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {b.books.map((book) => (
                <BookCard key={book.id} book={book} onOpen={setOpenBook} />
              ))}
            </div>
          </section>
        ))}
      </main>

      {openBook && (
        <BookDetailModal book={openBook} onClose={() => setOpenBook(null)} />
      )}
    </div>
  );
}
